import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Globe, ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function RecentWebsiteLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/contacts`);
      const websiteLeads = (res.data || [])
        .filter((c) => (c.lead_source || "").toLowerCase() === "website")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setLeads(websiteLeads);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // light auto-refresh so you can see new leads land live
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="shadow-sm" data-testid="recent-website-leads-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#00A3E0]" />
            Recent Website Leads
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/contacts?source=website")}
            data-testid="view-all-website-leads-btn"
          >
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-400 py-6 text-center">Loading…</p>
        ) : leads.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-slate-500">No website leads yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Submissions from your website will appear here within seconds.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {leads.map((lead) => (
              <button
                key={lead.id}
                className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-[#00A3E0] hover:bg-sky-50/50 transition-all group"
                onClick={() => navigate(`/contacts/${lead.id}`)}
                data-testid={`website-lead-${lead.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-slate-800 truncate group-hover:text-[#00A3E0]">
                      {lead.name || "Unnamed"}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                      {lead.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </span>
                      )}
                      {lead.email && (
                        <span className="flex items-center gap-1 truncate max-w-[200px]">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </span>
                      )}
                    </div>
                    {lead.address && (
                      <p className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5 truncate">
                        <MapPin className="w-3 h-3" />
                        {lead.address}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400 flex-shrink-0 whitespace-nowrap">
                    {fmtAgo(lead.created_at)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
